import { z } from 'zod';
import {
  CalendarDateSchema,
  DeletionStatusSchema,
  IdSchema,
  InputTypeSchema,
  InstantSchema,
  JobOrderNoSchema,
  KitIdSchema,
  OmittedIdentityIdSchema,
  OmittedIdentitySchema,
  OmittedMoneySchema,
  OriginSchema,
  QrCodeSchema,
  RowVersionSchema,
  StageSchema,
  ToothNumberSchema,
} from './common.schema';
import { SpanSchema } from './span.schema';
import { DepartmentSchema } from './user.schema';
import { NOTE_KINDS, PHOTO_KINDS } from '../../src/domain/kit/kit';
import { SPEC_FIELD_MAX, SPEC_FIELD_REFUSAL } from '../../src/domain/kit/specField';

export { NOTE_KINDS, PHOTO_KINDS };

/**
 * How much of a property a case calls for. Low, Medium, High.
 *
 * ONE scale for all four rows of the specification, ascending, spelled out.
 * The paper form used single letters and ran them in different directions on
 * different rows, which is how the client came to ask what one of them meant.
 * Words cannot be misread and one direction cannot be misordered.
 *
 * Occlusal stain shares it at the client's instruction. An earlier cut gave
 * stain its own `none | light | medium | heavy`, on the reasoning that "no
 * staining" is a different instruction from "a little"; the client chose one
 * consistent scale instead. If "no staining at all" ever needs saying, it
 * belongs in the instructions field, not as a fourth step on this axis.
 */
export const SpecScaleSchema = z.enum(['low', 'medium', 'high']);

/** The case specification, as it reads on the lab's paper job order. */
export const KitSpecificationSchema = z.object({
  translucency: SpecScaleSchema,
  anatomy: SpecScaleSchema,
  incisal: SpecScaleSchema,
  occlusal: SpecScaleSchema,

  /** Free text, all three. The lab writes in a batch or a product code. */
  ingot: z.string(),
  block: z.string(),
  stain: z.string(),
});

/**
 * Recording the ingot press or the zircon block from the bench that chose it.
 * Section 1 of the request of 13 September 2026.
 *
 * At least one of the two, each at most sixty characters, and an EMPTY STRING
 * is a valid value that clears the field: somebody who recorded the wrong ingot
 * must be able to take it back out. Authorised per FIELD, not per request, so a
 * caller sending both who owns only one is refused outright rather than half
 * applied.
 */
export const SetKitSpecificationSchema = z
  .object({
    ingot: z.string().max(SPEC_FIELD_MAX).optional(),
    block: z.string().max(SPEC_FIELD_MAX).optional(),
  })
  .refine((body) => body.ingot !== undefined || body.block !== undefined, {
    path: ['ingot'],
    message: SPEC_FIELD_REFUSAL.nothingSent,
  });

/**
 * What physically arrived with the case: the five tick boxes.
 *
 * There was a write-in line beside them, `other`, until the client asked for
 * one notes section at intake (request of 13 September 2026, section 3).
 * Anything that used to go on that line goes in the instructions now, and the
 * server's migration moved every existing line there rather than dropping it.
 */
export const ItemsReceivedSchema = z.object({
  impression: z.boolean(),
  model: z.boolean(),
  bite: z.boolean(),
  implant: z.boolean(),
  abutment: z.boolean(),
});

export { ToothNumberSchema };

/** FDI numbers as strings, e.g. ["14","15","16"]. */
export const TeethSchema = z.array(ToothNumberSchema);

/**
 * The catalogue item on ONE tooth.
 *
 * A tray used to carry a single item for every tooth on it, which is not how a
 * case arrives. Six units for one patient is regularly three Zircon crowns,
 * two E.max veneers and a post; reception could record only the one item the
 * dropdown allowed and the rest went into the instructions as a sentence,
 * where no report counts it and the bench has to read prose to find out what
 * it is making.
 *
 * `product` is never empty. A tooth that named no item of its own takes the
 * case's, and the server resolves that before this leaves it, so no client has
 * to know the fallback rule.
 */
export const ToothItemSchema = z.object({
  tooth: ToothNumberSchema,
  product: z.string(),
  /** The catalogue item's id, which survives a rename. */
  catalogItemId: IdSchema,
});

/**
 * What an action answers with when it can be taken back. Section 6 of the
 * request of 5 October 2026.
 */
export const UndoRefSchema = z.object({
  id: IdSchema,
  until: InstantSchema,
});

export const KitSchema = z.object({
  id: KitIdSchema,
  qrCode: QrCodeSchema,
  jobOrderNo: JobOrderNoSchema,
  stage: StageSchema,
  input: InputTypeSchema,
  urgent: z.boolean(),
  rework: z.boolean(),
  /**
   * The order's hold, projected onto the kit, so a queue row and the job view
   * know without a second call. While it is held, start, complete, flag and
   * Final Check are refused with 409 "This case is on hold." Section 2.2 of the
   * system test handover; section 9 of the review of 2 October 2026.
   */
  held: z.boolean(),
  holdReason: z.string().nullable(),
  product: z.string(),
  /**
   * The catalogue item, by id. `product` is its name, which follows a rename;
   * the id is what a case keeps. Section 2.5 of the system test handover.
   */
  catalogItemId: IdSchema,
  teeth: TeethSchema,
  /**
   * The item on each tooth, in the same order as `teeth`, so `teeth[i]` and
   * `toothItems[i]` are the same tooth.
   *
   * Alongside `teeth` rather than replacing it: most of what reads the chart
   * wants the FDI numbers and nothing else -- the odontogram, the printed
   * teeth line, the sort -- and making all of them destructure an object to
   * get back to a string would be a lot of churn for no gain.
   */
  toothItems: z.array(ToothItemSchema),
  /**
   * How many teeth this tray covers.
   *
   * Not `teeth.length`. A case can be opened on a count alone, before anybody
   * has marked the chart, so the number the technician works to and the FDI
   * numbers are two separate facts. Where the chart is filled in they agree.
   */
  units: z.number().int().positive(),
  /**
   * The unit price the case was taken in at. A price change on the catalogue
   * applies to new cases only. Absent without pay or admin, section 7.10 item 2.
   */
  unitPriceAtOrder: OmittedMoneySchema,
  /**
   * The bridges and titanium bars on this kit's order, projected from the
   * order the way `held` is, so the job view and Final Check draw them without
   * a second call.
   */
  spans: z.array(SpanSchema),
  shade: z.string(),
  specification: KitSpecificationSchema,
  itemsReceived: ItemsReceivedSchema,
  /**
   * The instructions on the case: ONE field. Section 5 of the request of
   * 5 October 2026.
   *
   * There were two, this and `labInstructions` on the order (section 9 of the
   * request of 20 September 2026). The second is gone from the contract. A
   * migration merged every case that had text in both into this one, each part
   * under its own heading ("Doctor's instructions:" and "Lab instructions:"),
   * so nothing was lost. Up to 5,100 characters, which is the longest a merged
   * pair can be.
   */
  instructions: z.string(),
  assignedToUserId: IdSchema.nullable(),
  /**
   * The assignee's name, so no screen has to resolve an id. Null when
   * unassigned. Mirrors `holderName` on the job order, which exists for the
   * same reason. Section 1.4 of the request of 13 September 2026.
   */
  assignedToUserName: z.string().nullable(),
  /**
   * Who gave the case to the person holding it: the assigner, or the holder
   * themselves when they took it by scanning. Null when nobody holds it, and on
   * a case assigned before this was recorded. The take-over warning names this
   * person. Section 9 of the request of 5 October 2026.
   */
  assignedByUserId: IdSchema.nullable().optional(),
  assignedByUserName: z.string().nullable().optional(),
  /**
   * The CALLER'S own last action on this case, while it can still be undone:
   * the id to send to `POST /work-undo/{undoId}` and the moment the server
   * stops accepting it. Null for everybody else, once the moment has passed,
   * and once anybody has changed the case since. Section 6 of the request of
   * 5 October 2026.
   */
  undo: UndoRefSchema.nullable().optional(),
  /**
   * Where the case comes from, projected from the order as the spans are.
   * Section 1 of the request of 24 September 2026, in place of the country.
   */
  origin: OriginSchema,
  daysLeft: z.number().int(),
  minutesInStage: z.number().int().nonnegative(),

  // Section 7.10 item 1. Omitted from the payload for a caller without
  // seeIdentity. Absent at the source, not blanked out later.
  dentistName: OmittedIdentitySchema,
  patientName: OmittedIdentitySchema,
  /**
   * The doctor's and the patient's records, projected from the order. Sent only
   * where the two names are; null on a case that was never linked to a record.
   * The request of 6 October 2026 (second), items 4 and 5.
   */
  doctorId: OmittedIdentityIdSchema,
  patientId: OmittedIdentityIdSchema,

  sentAt: InstantSchema.nullable(),
  startDate: CalendarDateSchema,
  deliveryDate: CalendarDateSchema,
  /**
   * When the case arrived at the lab: the moment its job order was booked in.
   * The request of 7 October 2026, section 4.
   */
  receivedAt: InstantSchema.nullable().optional(),
  /**
   * The job order this case remakes, projected from the order, and the doctor's
   * clinic: two columns a bench and Final Check can switch on. The lab's request
   * of 11 October 2026. `clinic` is identity: absent without `seeIdentity`, as
   * the two names are, and null on a case tied to no doctor's record.
   */
  redoOf: z.string().nullable().optional(),
  clinic: z.string().nullable().optional(),
  createdByUserId: IdSchema.nullable(),
  photoCount: z.number().int().nonnegative(),
  noteCount: z.number().int().nonnegative(),
  /**
   * The deletion on this kit's order, projected from it the way `spans` is, so
   * the job view can say a deletion is waiting without a second call. A kit of a
   * deleted case is not served at all, so this reads `none` or `pending`.
   */
  deletionStatus: DeletionStatusSchema,
  /**
   * This kit's order is archived, projected from it as the deletion is, so the
   * job view can say so and offer nothing to start: every command that works a
   * tray refuses an archived one. Section 4 of the request of 24 September 2026.
   */
  archived: z.boolean(),
  rowVersion: RowVersionSchema,
});

export const StageHistoryEntrySchema = z.object({
  id: IdSchema,
  /** The tray the entry is of. The request of 7 October 2026, section 4. */
  kitId: IdSchema.optional(),
  stage: StageSchema,
  byUserId: IdSchema.nullable(),
  byUserName: z.string().nullable(),
  startedAt: InstantSchema,
  endedAt: InstantSchema.nullable(),
  /**
   * The time this piece of work took, in seconds, computed BY THE SERVER from
   * its own timestamps.
   *
   * It used to be active time, excluding idle, as reported by the device. Section
   * 1 of the request of 20 September 2026 made the timer wall clock and stopped
   * the client sending a figure at all: this is now the seconds between Start and
   * Pause or Done, summed over every run of the stage, and it includes lunch, a
   * phone call and a forgotten tab. That is the client's instruction and section
   * 1.3 records what it costs.
   *
   * Still zero while the entry is open. A running task's elapsed time comes from
   * `startedAt` on the active task, not from here.
   */
  activeSeconds: z.number().int().nonnegative(),
  note: z.string().nullable(),
});

/**
 * One note about a case, whatever it was stored as. Section 3 of the request of
 * 16 September 2026.
 *
 * `GET /kits/{kitId}/notes` is a READ MODEL: it answers every note about the
 * case, the free ones and the reasons a rework, a flag, a hold, a deletion or a
 * redo left behind, in one shape and newest first. Nothing is copied into the
 * notes table; a rework reason still lives on the history entry it closed,
 * which is what the Sent back from Final Check banner reads.
 *
 * The four fields answer the client's four questions: what the note says, what
 * kind of note it is, where it came from and who it is for.
 */
export const KitNoteSchema = z.object({
  id: IdSchema,
  /**
   * Null for a note the lab wrote rather than a person: a hold reason is
   * recorded against the order and not against whoever typed it, and inventing
   * an author would be worse than saying there is none.
   */
  byUserId: IdSchema.nullable(),
  byUserName: z.string(),
  text: z.string(),
  createdAt: InstantSchema,
  kind: z.enum(NOTE_KINDS),
  /** The stage it was written at. Null for an order-level note such as a hold. */
  fromStage: StageSchema.nullable(),
  /** The stage it is addressed to. Set on a rework or a flag; null otherwise. */
  toStage: StageSchema.nullable(),
  /**
   * The system's sentence for an event in Lab Notes, such as "Assigned to
   * Jessica" or "Flagged at Wax, sent to Ceramic"; null for a note somebody
   * wrote. The words that came with the event are `text`. Request of
   * 4 October 2026, section 7.
   */
  event: z.string().nullable(),
  /** Where the writer works, shown beside their name. Null when there is no writer. */
  byUserDepartment: DepartmentSchema.nullable(),
  /**
   * The one person a private note is for, or null when everybody on the case
   * sees it. Seen by its writer, this person, and management and supervisors;
   * the server leaves it out of every other response.
   */
  visibleTo: z.object({ id: IdSchema, name: z.string() }).nullable(),
  /**
   * When the action this event records was undone, and by whom. The entry is
   * never removed: it stays, marked, so the history says what happened and that
   * it was taken back. Null on one that stands. Section 6 of the request of
   * 5 October 2026.
   */
  undoneAt: InstantSchema.nullable().optional(),
  undoneByName: z.string().nullable().optional(),
  /**
   * The stage visit an event belongs to. Set on a proof row (whose photographs
   * are the proofs carrying the same id), and on done, resolved, flag, rework
   * and a take-over's move; null on a note somebody wrote and on every row read
   * off the order. The request of 5 October 2026 (third), section 6.
   */
  historyEntryId: IdSchema.nullable().optional(),
});

/** `GET /kits/{kitId}/notes`: newest first, and how far the caller has read. */
export const NoteListSchema = z.object({
  items: z.array(KitNoteSchema),
  /** When the caller last opened Lab Notes on this case; null for never. */
  lastReadAt: InstantSchema.nullable(),
});

/**
 * Writing a free note. Unchanged: somebody types text and nothing else, and the
 * server stamps the kind and the stage from where they are.
 */
export const CreateKitNoteSchema = z.object({
  text: z.string().min(1, 'Write something first'),
  /**
   * Private to one person. Management and supervisors may write to anybody;
   * anybody else only to somebody who wrote to them privately on this case,
   * otherwise 403. Request of 4 October 2026, section 7.
   */
  visibleToUserId: IdSchema.nullish(),
});

/**
 * A photograph on a case. Two kinds share the row: a case photograph, taken at
 * intake or on the bench, and a proof of the finished work, taken at Mark done
 * against the piece of work it proves. Same store, same upload, same purge;
 * arranged separately, with its own endpoint and its own permission. Section 2
 * of the request of 13 September 2026.
 */
export const CasePhotoSchema = z.object({
  id: IdSchema,
  url: z.string(),
  thumbnailUrl: z.string(),
  uploadedByUserId: IdSchema,
  /** Who took it. The id is already here; the name is what a screen shows. */
  uploadedByUserName: z.string(),
  uploadedAt: InstantSchema,
  stage: StageSchema,
  kind: z.enum(PHOTO_KINDS),
  /** The piece of work this proves. Null on a case photograph. */
  historyEntryId: IdSchema.nullable(),
});

/**
 * The photos on a case, in the order they were attached.
 *
 * Not paged. A case carries a handful: the intake photographs reception took
 * of the impression or the patient, and whatever the bench added on the way
 * through. Paging a list that short would be ceremony.
 */
export const PhotoListSchema = z.object({ items: z.array(CasePhotoSchema) });

/* ---- the four work commands, section 7.4 ---- */

/**
 * One task running now.
 *
 * `startedAt` is when the CURRENT run began, per the server, and since section 1
 * of the request of 20 September 2026 it is what the clock on screen is computed
 * from. Unchanged in shape.
 */
export const ActiveTaskSchema = z.object({
  kitId: KitIdSchema,
  startedAt: InstantSchema,
});

/**
 * A task running now, as `GET /kits/running` answers it: enough to draw the
 * running strip and every live clock without a call per kit.
 *
 * `accumulatedSeconds` is what the open history entry already holds from earlier
 * runs of the same stage, so a task paused before lunch and taken up after it
 * reads the sum rather than starting again from zero. Zero on a first start,
 * which is the ordinary case.
 */
export const RunningTaskSchema = ActiveTaskSchema.extend({
  accumulatedSeconds: z.number().int().nonnegative(),
  jobOrderNo: JobOrderNoSchema,
  stage: StageSchema,
});

export const RunningTaskListSchema = z.object({ items: z.array(RunningTaskSchema) });

export const StartWorkSchema = z.object({
  startedAt: InstantSchema,
  source: z.enum(['qr', 'manual']),
  /**
   * Move the case to the caller's own bench first, then start it. Section 7 of
   * the request of 20 September 2026.
   *
   * Requires the `takeOver` permission and is REFUSED without it rather than
   * ignored: it moves a case off somebody else's bench, which is not a field a
   * caller should be able to send hopefully. Ignored on a kit the caller already
   * works, where there is nothing to move.
   */
  takeOver: z.boolean().optional(),
});

export const StartWorkResultSchema = z.object({
  kit: KitSchema,
  historyEntry: StageHistoryEntrySchema,
  activeTask: ActiveTaskSchema,
});

/**
 * Pause carries nothing.
 *
 * It used to carry `activeSeconds`, the device's own count. Section 1.4 of the
 * request of 20 September 2026: the server computes the entry's time from its own
 * `startedAt` and the moment the pause arrives, and it is the only source. A
 * client still sending the field is IGNORED rather than refused, so a handset
 * running yesterday's build keeps working through its service worker update:
 * unknown keys are stripped, not rejected.
 */
export const PauseWorkSchema = z.object({});

/**
 * `POST /kits/{kitId}/stop`: a manager stops the clock somebody else has
 * running on a tray, from the board. Request of 25 September 2026.
 *
 * A pause made on the holder's behalf: the run so far is credited to them by
 * the server's clock, their entry stays open at the bench, and scanning the
 * tray takes it up again. Nothing moves and nothing is marked done. Requires
 * `stopWork`; 409 when no clock is running on the tray. The body carries
 * nothing. `historyEntry` is the holder's entry, still open, with what it has
 * come to so far, so the screen can say whose clock it was and how long it ran.
 */
export const StopWorkResultSchema = z.object({
  kit: KitSchema,
  historyEntry: StageHistoryEntrySchema,
});

/**
 * `nextStage` is only accepted when the kit is flagged or in rework, in which
 * case it is required. For a normal kit the server ignores it and advances
 * along routeFor(kit.input). Section 7.4.
 *
 * `activeSeconds` is gone, as on Pause and for the same reason.
 */
export const CompleteWorkSchema = z.object({
  completedAt: InstantSchema,
  /** A note for Lab Notes, optional. Request of 4 October 2026, section 5. */
  note: z.string().max(2000).nullish(),
  nextStage: StageSchema.nullish(),
  /**
   * Hand the case to this person at the destination. Section 5 of the request of
   * 20 September 2026.
   *
   * They must work that bench: the server checks it with the same rule as
   * `POST /kits/{kitId}/assign` and refuses with the same wording. Null or absent
   * is "nobody in particular", which is what most completions are: the case lands
   * on the bench and whoever picks it up scans it.
   */
  assignToUserId: IdSchema.nullish(),
});

export const CompleteWorkResultSchema = z.object({
  kit: KitSchema,
  historyEntry: StageHistoryEntrySchema,
  nextStage: StageSchema.nullable(),
});

/**
 * Flagging is not a QC failure, so `rework` stays false. The note is required:
 * a flag with no reason is a case nobody can act on.
 *
 * `activeSeconds` is gone here too. Section 1.4 names Pause and Mark done, and
 * this is the third command that closes a history entry: leaving it carrying a
 * count the client no longer computes would have made Flag the one place where
 * the time on an entry came from a device.
 */
export const FlagSchema = z.object({
  toStage: StageSchema,
  note: z.string().min(1, 'Say what is wrong before sending it on'),
  /** As on Mark done: hand it to a named person at the destination. Section 5.2. */
  assignToUserId: IdSchema.nullish(),
});

export const AssignKitSchema = z.object({
  userId: IdSchema.nullable(),
  /** A note that comes with the assignment, written into Lab Notes. Request of 4 October 2026. */
  note: z.string().max(2000).nullish(),
  /** Who sees the note: everybody on the case (the default), or only the technician assigned. */
  noteVisibility: z.enum(['everyone', 'assignee']).optional(),
  /**
   * Earlier private notes on this case to forward to the person assigned, who
   * would not otherwise see them. Management and supervisors only.
   */
  forwardNoteIds: z.array(IdSchema).optional(),
});

export const MarkUrgentSchema = z.object({
  urgent: z.boolean(),
  reason: z.string().optional(),
});

export const QcReworkSchema = z.object({
  toStage: StageSchema,
  note: z.string().min(1, 'Say what failed before sending it back'),
  /**
   * The technician the rework is for, chosen as an assignment is: the case is
   * given to them at `toStage` and they are told. Left out, it goes to the
   * department and whoever last worked it there is told, as before. 404 for an
   * unknown or revoked person. Section 8 of the request of 5 October 2026.
   */
  assignToUserId: IdSchema.optional(),
});

/**
 * `POST /work-undo/{undoId}`: what was put back, and what could not be. One
 * case for an action on one, several for an action on a selection. Section 6 of
 * the request of 5 October 2026.
 */
export const UndoResultSchema = z.object({
  items: z.array(KitSchema),
  refused: z.array(z.object({ kitId: IdSchema, detail: z.string() })),
});

export type Kit = z.infer<typeof KitSchema>;
export type UndoResult = z.infer<typeof UndoResultSchema>;
export type StageHistoryEntry = z.infer<typeof StageHistoryEntrySchema>;
export type KitNote = z.infer<typeof KitNoteSchema>;
export type CasePhoto = z.infer<typeof CasePhotoSchema>;
export type RunningTask = z.infer<typeof RunningTaskSchema>;
export type StartWorkCommand = z.infer<typeof StartWorkSchema>;
export type CompleteWorkCommand = z.infer<typeof CompleteWorkSchema>;
export type FlagCommand = z.infer<typeof FlagSchema>;
